import React from 'react';
import { Card } from './ui/Card';
import { Eye, Filter } from 'lucide-react';
import { AnimatedNumber } from './AnimatedNumber';
import { motion } from 'framer-motion';
interface ObservationsData {
  lifeThreading: number;
  major: number;
  moderate: number;
  minor: number;
  resolved: number;
}
interface ObservationsCardProps {
  data: ObservationsData;
  isFiltered?: boolean;
}
export function ObservationsCard({
  data,
  isFiltered
}: ObservationsCardProps) {
  const total = data.lifeThreading + data.major + data.moderate + data.minor + data.resolved;
  return <Card className="h-full flex flex-col" data-id="element-1021" data-mp="i289avtc2sek">
      <div className="flex justify-between items-start mb-4" data-id="element-1022" data-mp="m5xzfppnpi1p">
        <div className="flex items-center gap-2 flex-wrap" data-id="element-1023" data-mp="qwwdsjr3462b">
          <div data-id="element-1024" data-mp="qyh7y89hlmzn">
            <h3 className="font-bold text-gray-900 text-sm mb-1" data-id="element-1025" data-mp="qz0rtuip6lgb">
              Open Observations
            </h3>
            <p className="text-xs text-gray-500" data-id="element-1026" data-mp="3qil6yp5hqo8l">Last 7 weeks</p>
          </div>
          {isFiltered && <motion.div initial={{
          opacity: 0,
          scale: 0.8
        }} animate={{
          opacity: 1,
          scale: 1
        }} className="flex items-center gap-1 bg-amber-100 text-amber-700 text-[10px] font-bold uppercase tracking-wide px-2 py-0.5 rounded-full" data-id="element-1027" data-mp="1fjv9oy7yxa5x">
              <Filter size={10} data-id="element-1028" data-mp="10t4klxxrgp6i" />
              Filtered
            </motion.div>}
        </div>
        <button className="flex items-center gap-1.5 text-xs font-medium text-gray-600 border border-gray-200 rounded px-2.5 py-1 hover:bg-gray-50 transition-colors" data-id="element-1029" data-mp="vtjbwvssh2gl">
          <Eye size={14} data-id="element-1030" data-mp="mvpoybgwzl63" />
          View all
        </button>
      </div>

      <div className="flex-1 flex items-center justify-center" data-id="element-1031" data-mp="hx4qaghb99jr">
        <div className="relative w-40 h-40" data-id="element-1032" data-mp="2h97357vfj5og">
          {/* Donut Chart Placeholder */}
          <svg viewBox="0 0 100 100" className="transform -rotate-90" data-id="element-1033" data-mp="2h6upv80m9jhq">
            <circle cx="50" cy="50" r="40" fill="none" stroke="#e5e7eb" strokeWidth="12" data-id="element-1034" data-mp="2canlvkrs6i2f" />
            <circle cx="50" cy="50" r="40" fill="none" stroke="#fbbf24" strokeWidth="12" strokeDasharray="251.2" strokeDashoffset="0" data-id="element-1035" data-mp="fcgh4mo23uj5" />
          </svg>
          <div className="absolute inset-0 flex flex-col items-center justify-center" data-id="element-1036" data-mp="tb6dg031xq6g">
            <AnimatedNumber value={total} className="text-4xl font-bold text-gray-900" data-id="element-1037" data-mp="yb2k6734vvpp" />
            <span className="text-xs text-gray-500" data-id="element-1038" data-mp="jcgu02vibxo3">Total</span>
          </div>
        </div>
      </div>

      <div className="space-y-2 mt-4" data-id="element-1039" data-mp="ob1u82arehcj">
        <div className="flex items-center justify-between text-sm" data-id="element-1040" data-mp="oe651iiqcbc6">
          <div className="flex items-center gap-2" data-id="element-1041" data-mp="341xxwtjneo88">
            <div className="w-3 h-3 bg-red-500 rounded-full" data-id="element-1042" data-mp="3933pup5hi7gp" />
            <span className="text-gray-700" data-id="element-1043" data-mp="390hj2rqf3vzj">Life threading</span>
          </div>
          <AnimatedNumber value={data.lifeThreading} className="font-semibold" data-id="element-1044" data-mp="1r3e57vy5tib0" />
        </div>
        <div className="flex items-center justify-between text-sm" data-id="element-1045" data-mp="1m88iiuwxnzlv">
          <div className="flex items-center gap-2" data-id="element-1046" data-mp="2053xt53s2nad">
            <div className="w-3 h-3 bg-orange-500 rounded-full" data-id="element-1047" data-mp="1w3in9qepul3c" />
            <span className="text-gray-700" data-id="element-1048" data-mp="1w1xqdu4xiq2b">Major</span>
          </div>
          <AnimatedNumber value={data.major} className="font-semibold" data-id="element-1049" data-mp="18f93sv4z69fd" />
        </div>
        <div className="flex items-center justify-between text-sm" data-id="element-1050" data-mp="13gfqp3tlokfq">
          <div className="flex items-center gap-2" data-id="element-1051" data-mp="1h6j9tlx6ny0y">
            <div className="w-3 h-3 bg-amber-500 rounded-full" data-id="element-1052" data-mp="1h82oj836y10m" />
            <span className="text-gray-700" data-id="element-1053" data-mp="1cft0jxlmzb0z">Moderate</span>
          </div>
          <AnimatedNumber value={data.moderate} className="font-semibold" data-id="element-1054" data-mp="oq8j5j54sltt" />
        </div>
        <div className="flex items-center justify-between text-sm" data-id="element-1055" data-mp="kovc55qayclb">
          <div className="flex items-center gap-2" data-id="element-1056" data-mp="komxp9ej6z80">
            <div className="w-3 h-3 bg-gray-400 rounded-full" data-id="element-1057" data-mp="xemhsqi2ub01" />
            <span className="text-gray-700" data-id="element-1058" data-mp="xc9r9oy96sbd">Minor</span>
          </div>
          <AnimatedNumber value={data.minor} className="font-semibold" data-id="element-1059" data-mp="12dqq7ex6g4pb" />
        </div>
        <div className="flex items-center justify-between text-sm" data-id="element-1060" data-mp="17b8zx7j0qnyd">
          <div className="flex items-center gap-2" data-id="element-1061" data-mp="1c2qv1eyd1ubf">
            <div className="w-3 h-3 bg-emerald-500 rounded-full" data-id="element-1062" data-mp="1c4bm2ulguv4j" />
            <span className="text-gray-700" data-id="element-1063" data-mp="1h121rgtczopf">Resolved (last 2 weeks)</span>
          </div>
          <AnimatedNumber value={data.resolved} className="font-semibold" data-id="element-1064" data-mp="1l2xdzgctvmhz" />
        </div>
      </div>
    </Card>;
}